<script setup>
import { defineProps, getCurrentInstance } from 'vue';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import RadioButton from 'primevue/radiobutton';
import Calendar from 'primevue/calendar';
import Divider from 'primevue/divider';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const brandedContentOptions = [
    { label: $t('No'), value: false },
    { label: $t('Yes'), value: true },
];

const props = defineProps({
    form: {
        type: Object,
        required: true
    },
    categories: {
        type: Array,
        required: true
    },
    languages: {
        type: Array,
        required: true
    },
    countries: {
        type: Array,
        required: true
    },
    sellers: {
        type: Array,
        default: () => []
    },
    linkOptions: {
        type: Array,
        required: true
    },
    linkLimitOptions: {
        type: Array,
        required: true
    },
    imageLimitOptions: {
        type: Array,
        required: true
    },
    formatDate: {
        type: Function,
        required: false
    },
    // URL validation props (for Create mode)
    urlValidation: {
        type: Object,
        default: () => ({
            status: '',
            message: '',
            sites: [],
            checking: false
        })
    },
    checkDuplicateSites: {
        type: Function,
        default: () => {}
    },
    // Flag to show/hide certain fields
    showUrlValidation: {
        type: Boolean,
        default: true
    },
    showSeller: {
        type: Boolean,
        default: true
    },
    showEntryInfo: {
        type: Boolean,
        default: false
    }
});
</script>

<template>
    <div class="site-form-inputs-component grid grid-cols-12 gap-3">
        <h3 class="text-base font-semibold col-span-12">{{ $t('Basic informations') }}</h3>

        <!-- URL, Link Type, Categoria -->
        <div class="col-span-12 lg:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('URL') }} <span class="required-field">*</span>
            </label>
            <div class="relative">
                <InputText
                    v-model="form.url"
                    class="w-full"
                    :class="{
                        'p-invalid': urlValidation.status === 'error'
                    }"
                    :style="urlValidation.status === 'warning' ? 'border-color: #f59e0b;' : ''"
                    required
                    placeholder="https://exemplo.com"
                    @blur="showUrlValidation ? checkDuplicateSites() : null"
                />
                <i v-if="urlValidation.checking" class="pi pi-spin pi-spinner absolute right-3 top-1/3" />
            </div>
            <small v-if="form.errors.url" class="text-red-600">{{ form.errors.url }}</small>
            <small v-if="showUrlValidation && urlValidation.status === 'error'" class="p-error block mt-1">
                <i class="pi pi-exclamation-triangle mr-1" />
                {{ urlValidation.message }}
            </small>
            <small v-if="showUrlValidation && urlValidation.status === 'warning'" class="block mt-1" style="color: #f59e0b;">
                <i class="pi pi-exclamation-triangle mr-1" />
                {{ urlValidation.message }}
            </small>
        </div>

        <div class="col-span-12 md:col-span-6 lg:col-span-2">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Link type') }} <span class="required-field">*</span>
            </label>
            <Select
                required
                v-model="form.link_type"
                :options="linkOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="$t('Select...')"
                class="w-full"
            />
            <small v-if="form.errors.link_type" class="text-red-600">{{ form.errors.link_type }}</small>
        </div>

        <div class="col-span-12 md:col-span-6 lg:col-span-2">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Branded Content') }}
            </label>
            <Select
                v-model="form.branded_content"
                :options="brandedContentOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="$t('Select...')"
                class="w-full"
            />
            <small v-if="form.errors.branded_content" class="text-red-600">{{ form.errors.branded_content }}</small>
        </div>

        <div class="col-span-12 md:col-span-6 lg:col-span-2">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Category') }}
            </label>
            <Select
                v-model="form.category_id"
                :options="categories"
                optionLabel="name"
                optionValue="id"
                :placeholder="$t('Select...')"
                class="w-full"
            />
            <small v-if="form.errors.category_id" class="text-red-600">{{ form.errors.category_id }}</small>
        </div>

        <!-- List of found sites (if URL validation is enabled) -->
        <div v-if="showUrlValidation && urlValidation.sites.length > 0" class="col-span-12 mt-[-8px]">
            <div class="p-2.5 bg-surface-50 dark:bg-surface-800 rounded-md border border-surface-200 dark:border-surface-700">
                <div class="text-sm font-medium mb-1.5 text-surface-600 dark:text-surface-300">
                    {{ urlValidation.status === 'error' ? $t('Site cadastrado:') : `${urlValidation.sites.length} ${$t('Sites semelhantes encontrados:')}` }}
                </div>
                <ul class="space-y-1 max-h-32 overflow-y-auto">
                    <li v-for="site in urlValidation.sites" :key="site.id" class="flex items-center gap-2 text-sm">
                        <i class="pi pi-link text-xs text-surface-500" />
                        <a
                            :href="site.url.startsWith('http') ? site.url : `https://${site.url}`"
                            target="_blank"
                            class="text-primary-500 hover:text-primary-600 hover:underline"
                        >
                            {{ site.url }}
                        </a>
                        <span class="text-surface-500 text-xs">
                            (DA: {{ site.da || 'N/A' }}, DR: {{ site.dr || 'N/A' }})
                        </span>
                    </li>
                </ul>
            </div>
        </div>

        <!-- DA, DR, Traffic -->
        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('DA') }}
            </label>
            <InputNumber
                v-model="form.da"
                class="w-full"
                :min="0"
                :max="100"
                :minFractionDigits="0"
                :maxFractionDigits="2"
            />
            <small v-if="form.errors.da" class="text-red-600">{{ form.errors.da }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('DR') }}
            </label>
            <InputNumber
                v-model="form.dr"
                class="w-full"
                :min="0"
                :max="100"
                :minFractionDigits="0"
                :maxFractionDigits="2"
            />
            <small v-if="form.errors.dr" class="text-red-600">{{ form.errors.dr }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Traffic') }}
            </label>
            <InputNumber
                v-model="form.traffic"
                class="w-full"
                :min="0"
                :minFractionDigits="0"
                :maxFractionDigits="2"
            />
            <small v-if="form.errors.traffic" class="text-red-600">{{ form.errors.traffic }}</small>
        </div>

        <!-- Language and Country -->
        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Language') }}
            </label>
            <Select
                v-model="form.language_id"
                :options="languages"
                optionLabel="name"
                optionValue="id"
                :placeholder="$t('Select...')"
                class="w-full"
            />
            <small v-if="form.errors.language_id" class="text-red-600">{{ form.errors.language_id }}</small>
        </div>

        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Country') }}
            </label>
            <Select
                v-model="form.country_id"
                :options="countries"
                optionLabel="name"
                optionValue="id"
                :placeholder="$t('Select...')"
                class="w-full"
            />
            <small v-if="form.errors.country_id" class="text-red-600">{{ form.errors.country_id }}</small>
        </div>

        <!-- Divisor -->
        <Divider class="col-span-12 my-1" />
        <h3 class="text-base font-semibold col-span-12">{{ $t('Features') }}</h3>

        <!-- Checkboxes -->
        <div class="col-span-12">
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div class="flex items-center">
                    <Checkbox v-model="form.menu" inputId="menu" :binary="true" />
                    <label for="menu" class="ml-2 text-sm">{{ $t('Menu') }}</label>
                </div>
                <div class="flex items-center">
                    <Checkbox v-model="form.banner" inputId="banner" :binary="true" />
                    <label for="banner" class="ml-2 text-sm">{{ $t('Banner') }}</label>
                </div>
                <div class="flex items-center">
                    <Checkbox v-model="form.promo" inputId="promo" :binary="true" />
                    <label for="promo" class="ml-2 text-sm">{{ $t('Promo') }}</label>
                </div>
                <div class="flex items-center">
                    <Checkbox v-model="form.google_news" inputId="google_news" :binary="true" />
                    <label for="google_news" class="ml-2 text-sm">{{ $t('Google News') }}</label>
                </div>
                <div class="flex items-center">
                    <Checkbox v-model="form.embed" inputId="embed" :binary="true" />
                    <label for="embed" class="ml-2 text-sm">{{ $t('Embed') }}</label>
                </div>
            </div>
        </div>

        <!-- Conditional Checkboxes -->
        <div class="col-span-12 md:col-span-6">
            <div class="flex items-center mb-1.5">
                <Checkbox v-model="form.restrict" inputId="restrict" :binary="true" />
                <label for="restrict" class="ml-2 text-sm">{{ $t('Restriction') }}</label>
            </div>
            <InputText
                v-model="form.restrict_detail"
                class="w-full"
                :disabled="!form.restrict"
                :placeholder="$t('Inform about the restriction')"
            />
        </div>

        <div class="col-span-12 md:col-span-6">
            <div class="flex items-center mb-1.5">
                <Checkbox v-model="form.archive_article" inputId="archive_article" :binary="true" />
                <label for="archive_article" class="ml-2 text-sm">{{ $t('Archive article') }}</label>
            </div>
            <InputText
                v-model="form.archive_due"
                class="w-full"
                :disabled="!form.archive_article"
                :placeholder="$t('Inform how long')"
            />
        </div>

        <!-- Number of links and images -->
        <!-- <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Number of links in the article') }}
            </label>
            <div class="flex gap-3">
                <div
                    v-for="option in linkLimitOptions"
                    :key="option.value"
                    class="flex items-center"
                >
                    <RadioButton
                        v-model="form.links"
                        :inputId="`links-${option.value}`"
                        :value="option.value"
                    />
                    <label :for="`links-${option.value}`" class="ml-2 text-sm">{{ option.label }}</label>
                </div>
            </div>
        </div> -->

        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Maximum number of images in content') }}
            </label>
            <div class="flex gap-3">
                <div
                    v-for="option in imageLimitOptions"
                    :key="option.value"
                    class="flex items-center"
                >
                    <RadioButton
                        v-model="form.images"
                        :inputId="`images-${option.value}`"
                        :value="option.value"
                    />
                    <label :for="`images-${option.value}`" class="ml-2 text-sm">{{ option.label }}</label>
                </div>
            </div>
        </div>

        <Divider class="col-span-12 my-1" />

        <!-- Last Posted, Seller, Example Article -->
        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Last posted') }}
            </label>
            <Calendar
                v-model="form.last_posted"
                showIcon
                class="w-full"
                dateFormat="dd/mm/yy"
                :showTime="false"
                @update:modelValue="(value) => form.last_posted = formatDate ? formatDate(value) : value"
            />
            <small v-if="form.errors.last_posted" class="text-red-600">{{ form.errors.last_posted }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('WhatsApp Group') }}
            </label>
            <InputText
                v-model="form.wpp_group"
                class="w-full"
                placeholder="Nome do grupo do WhatsApp"
            />
            <small v-if="form.errors.wpp_group" class="text-red-600">{{ form.errors.wpp_group }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <div class="flex items-center justify-center h-full pt-6">
                <div>
                    <div class="flex items-center">
                        <Checkbox v-model="form.mark_as_published" inputId="mark_as_published" :binary="true" />
                        <label for="mark_as_published" class="ml-2 text-sm font-medium text-surface-700">
                            {{ $t('Mark as published') }}
                        </label>
                    </div>
                    <small v-if="form.errors.mark_as_published" class="text-red-600">{{ form.errors.mark_as_published }}</small>
                </div>
            </div>
        </div>

        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Validation date') }}
            </label>
            <Calendar
                v-model="form.validation_date"
                showIcon
                class="w-full"
                dateFormat="dd/mm/yy"
                @update:modelValue="(value) => form.validation_date = formatDate ? formatDate(value) : value"
            />
            <small v-if="form.errors.validation_date" class="text-red-600">{{ form.errors.validation_date }}</small>
        </div>

        <div v-if="showSeller" class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Seller') }}
            </label>
            <Select
                v-model="form.seller_id"
                :options="sellers"
                optionLabel="name"
                optionValue="id"
                :placeholder="$t('Select...')"
                class="w-full"
            />
            <small v-if="form.errors.seller_id" class="text-red-600">{{ form.errors.seller_id }}</small>
        </div>

        <div class="col-span-12">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Example article') }}
            </label>
            <InputText
                v-model="form.example_article"
                class="w-full"
                placeholder="https://..."
            />
            <small v-if="form.errors.example_article" class="text-red-600">{{ form.errors.example_article }}</small>
        </div>

        <!-- Obs, Rules for content -->
        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Obs') }}
            </label>
            <Textarea
                v-model="form.obs"
                rows="2"
                class="w-full"
            />
            <small v-if="form.errors.obs" class="text-red-600">{{ form.errors.obs }}</small>
        </div>

        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Rules for content') }}
            </label>
            <Textarea
                v-model="form.rules"
                rows="2"
                class="w-full"
            />
            <small v-if="form.errors.rules" class="text-red-600">{{ form.errors.rules }}</small>
        </div>
        <div class="col-span-12 md:col-span-12">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Type Observation') }}
            </label>
            <Textarea
                v-model="form.type_observation"
                rows="2"
                class="w-full"
            />
            <small v-if="form.errors.type_observation" class="text-red-600">{{ form.errors.type_observation }}</small>
        </div>

        <Divider class="col-span-12 my-1" />

        <h3 class="text-base font-semibold col-span-12">{{ $t('Owner Information') }}</h3>

        <!-- Owner name, Owner Role, Email -->
        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Owner name') }}
            </label>
            <InputText
                v-model="form.owner_name"
                class="w-full"
            />
            <small v-if="form.errors.owner_name" class="text-red-600">{{ form.errors.owner_name }}</small>
        </div>

        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Owner role') }}
            </label>
            <InputText
                v-model="form.owner_role"
                class="w-full"
            />
            <small v-if="form.errors.owner_role" class="text-red-600">{{ form.errors.owner_role }}</small>
        </div>

        <div class="col-span-12 md:col-span-6">
            <!-- Phone -->
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Owner phone') }}
            </label>
            <InputText
                v-model="form.owner_phone"
                class="w-full mb-3"
            />
            <small v-if="form.errors.owner_phone" class="text-red-600">{{ form.errors.owner_phone }}</small>

            <!-- Email -->
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Owner email') }}
            </label>
            <InputText
                v-model="form.owner_email"
                type="email"
                class="w-full"
            />
            <small v-if="form.errors.owner_email" class="text-red-600">{{ form.errors.owner_email }}</small>
        </div>

        <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Site bank') }}
            </label>
            <Textarea
                v-model="form.bank"
                class="w-full min-h-[122px]"
            />
            <small v-if="form.errors.bank" class="text-red-600">{{ form.errors.bank }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Global account') }}
            </label>
            <InputText
                v-model="form.global_account"
                class="w-full"
            />
            <small v-if="form.errors.global_account" class="text-red-600">{{ form.errors.global_account }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Site pix') }}
            </label>
            <InputText
                v-model="form.pix"
                class="w-full"
            />
            <small v-if="form.errors.pix" class="text-red-600">{{ form.errors.pix }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Site paypal') }}
            </label>
            <InputText
                v-model="form.paypal"
                class="w-full"
            />
            <small v-if="form.errors.paypal" class="text-red-600">{{ form.errors.paypal }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Site phone') }}
            </label>
            <InputText
                v-model="form.phone"
                class="w-full"
            />
            <small v-if="form.errors.phone" class="text-red-600">{{ form.errors.phone }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Site instagram') }}
            </label>
            <InputText
                v-model="form.instagram"
                class="w-full"
            />
            <small v-if="form.errors.instagram" class="text-red-600">{{ form.errors.instagram }}</small>
        </div>

        <div class="col-span-12 md:col-span-4">
            <label class="block text-sm font-medium text-surface-700 mb-1.5">
                {{ $t('Site facebook') }}
            </label>
            <InputText
                v-model="form.facebook"
                class="w-full"
            />
            <small v-if="form.errors.facebook" class="text-red-600">{{ form.errors.facebook }}</small>
        </div>

        <template v-if="showEntryInfo">
            <Divider class="col-span-12 my-1" />
            <h3 class="text-base font-semibold col-span-12">{{ $t('Entry information') }}</h3>

            <div class="col-span-12 md:col-span-6">
                <label class="block text-sm font-medium text-surface-700 mb-1.5">
                    {{ $t('Inserted Date') }}
                </label>
                <Calendar
                    v-model="form.inserted_at"
                    showIcon
                    class="w-full"
                    dateFormat="dd/mm/yy"
                    @update:modelValue="(value) => form.inserted_at = formatDate ? formatDate(value) : value"
                />
                <small v-if="form.errors.inserted_at" class="text-red-600">{{ form.errors.inserted_at }}</small>
            </div>

            <div class="col-span-12 md:col-span-6">
                <label class="block text-sm font-medium text-surface-700 mb-1.5">
                    {{ $t('Last updated at') }}
                </label>
                <Calendar
                    v-model="form.last_updated_at"
                    showIcon
                    class="w-full"
                    dateFormat="dd/mm/yy"
                    @update:modelValue="(value) => form.last_updated_at = formatDate ? formatDate(value) : value"
                />
                <small v-if="form.errors.last_updated_at" class="text-red-600">{{ form.errors.last_updated_at }}</small>
            </div>

            <div class="col-span-12 md:col-span-6">
                <label class="block text-sm font-medium text-surface-700 mb-1.5">
                    {{ $t('Created At') }}
                </label>
                <InputText
                    :model-value="form.created_at ? new Date(form.created_at).toLocaleDateString('pt-BR') : ''"
                    class="w-full bg-surface-100"
                    readonly
                    disabled
                />
            </div>

            <div class="col-span-12 md:col-span-6">
                <label class="block text-sm font-medium text-surface-700 mb-1.5">
                    {{ $t('Updated At') }}
                </label>
                <InputText
                    :model-value="form.updated_at ? new Date(form.updated_at).toLocaleDateString('pt-BR') : ''"
                    class="w-full bg-surface-100"
                    readonly
                    disabled
                />
            </div>
        </template>
    </div>
</template>

<style scoped>
.required-field {
    color: red;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}

:deep(.p-select) {
    height: 42px;
}

:deep(.p-select .p-select-label) {
    padding: 8px 12px;
    line-height: 1.5;
}
</style>
